import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Collapse, Input, message, Modal, Select, Switch } from 'antd';
import { Layer, Stage } from 'react-konva';
import { useStore } from '../../store/store';
import { filterSameColor, iconButtonStyle } from '../../utils/styles';
import { NavbarButton } from '../Layout/NavbarButton';
import { NavbarInputNumber } from '../Layout/NavbarInputNumber';
import { NavbarListButton } from '../Layout/NavbarListButton';
import NavbarSelector from '../Layout/NavbarSelector';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import ShapesStarEditIcon from '../common/ShapesStarEditIcon';
import './../common/Utils.css';
import ShapeFullscreenEditor from './ShapeFullscreenEditor';
import ShapeBankPicker from './ShapeBankPicker';
import { getShapeBank, postShapeBankItem } from '../../requests/documentDatas';
import {
  getShapeComponent,
  RADIUS_CAPABLE_SHAPES,
  CORNER_COUNT_BY_SHAPE,
  canLockShapeRatio,
  getPolygonRadius,
  getShadowCanvasMargin,
  isCompoundShape,
  SUB_SHAPE_COLOR,
  ZONE_COLOR,
  mutateMakeCompoundWithSingleSubShape,
  mutateCollapseUnchangedSubShape,
  mutateAddSubShape,
  mutateRemoveSubShape,
  mutateDuplicateSubShape,
  mutateReorderSubShape,
  mutateSetSubShapeKind,
  mutateCollapseCompoundToShapeKind,
  mutateRescaleSubShapes,
  mutateSetSubShapeSize,
  captureStageThumbnail,
  parseSvgFile,
  parseSvgViewBox,
  mutateAddImportedSubShape,
  mutateReplaceSubShapeImport,
  getCompoundDesignScale,
  shapeRadius,
} from './shapeUtils';
import { remapSubShapeIds } from './shapeLinks';
import { hydrateShapeTexts, mutateAddTextSubShape, serializeShapeTexts, TEXT_ALIGNS, TEXT_SUB_SHAPE, TEXT_VERTICAL_ALIGNS, textLayoutOf } from './shapeText';
import ShapeTextOverlay from './ShapeTextOverlay';
import ShapePanelSections from './ShapePanelSections';
import { convertFromRaw, EditorState } from 'draft-js';
import { decorator } from '../RichEditor/utils';
import ShapeKindSelector from './ShapeKindSelector';
import BoxActionsPanel from '../common/BoxActionsPanel';
import LabelledRow from '../common/LabelledRow';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import { BackgroundColorPicker } from '../Layout/BackgroundColorPicker';
import { BorderManager } from '../Layout/BorderManager';
import { SHAPES, SHAPE_KIND_LINE, getPrimaryDark, uuid } from '../../constants/constants';
import { sectionAccentColor } from '../../shared/ui/editorAccents';

const selector =
  (boxId) =>
    ({ updateBox, selectedBoxId, landscape, setBoxMovable, configuration, currentDocument, currentProposal }) => {
      return {
        updateBox,
        isSelected: selectedBoxId === boxId,
        landscape,
        setBoxMovable,
        configuration,
        currentDocument,
        currentProposal,
      };
    };

const Shape = ({ box }) => {
  const { isSelected, updateBox } = useStore(selector(box.id));
  const { t } = useTranslation();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Sous-forme créée en basculant une forme simple pour ouvrir l'éditeur (cf.
  // handleEnterShapeEditing) : si l'éditeur est refermé sans qu'elle ait changé,
  // la forme redevient simple.
  const convertedSubShapeIdRef = useRef(null);
  const openEditor = (convertedSubShapeId) => {
    convertedSubShapeIdRef.current = typeof convertedSubShapeId === 'string' ? convertedSubShapeId : null;
    setIsEditorOpen(true);
  };
  const closeEditor = () => {
    const convertedSubShapeId = convertedSubShapeIdRef.current;
    convertedSubShapeIdRef.current = null;
    if (convertedSubShapeId) {
      updateBox(box.id, (draft) => mutateCollapseUnchangedSubShape(draft, convertedSubShapeId));
    }
    setIsEditorOpen(false);
  };

  const isCompound = isCompoundShape(box);
  const subShapes = box.content?.shapes ?? [];

  // Vignette de sauvegarde en bibliothèque (cf. ShapeComponent ci-dessous) :
  // capture du Stage réellement affiché, pas un rendu Konva séparé. Pas de
  // Transformer à masquer ici (cf. plus bas, l'édition d'une sous-forme ne se
  // fait plus sur ce Stage) : `captureStageThumbnail` l'accepte à `null`.
  const stageRef = useRef(null);
  const handleGenerateThumbnail = () => captureStageThumbnail(stageRef.current, null);

  // Le Stage fait la taille de la box + cette marge de chaque côté quand une
  // ombre est active, sans quoi elle serait rognée au bord du canvas. Le
  // wrapper qui l'agrandit est purement visuel (position absolue, jamais
  // cliquable — cf. pointerEvents ci-dessous) : la box elle-même garde
  // exactement box.width/box.height, rien ne change pour la sélection, le
  // resize ou la mise en page environnante. Forme composée : la marge est
  // celle de la sous-forme qui en réclame le plus — un déplacement/
  // redimensionnement au-delà des bords de la box, lui, est rogné (cf.
  // getShapeComponent), pas compensé par une marge.
  const canvasMargin = isCompound
    ? Math.max(0, ...subShapes.map((s) => getShadowCanvasMargin(s.shadow)))
    : getShadowCanvasMargin(box.shadow);

  // Une forme composée ne s'édite plus qu'en plein écran (cf.
  // ShapeFullscreenEditor) : sélectionner/déplacer/redimensionner une
  // sous-forme directement sur ce Stage — au milieu du reste du document,
  // potentiellement zoomé (InfinitePage) — s'est révélé trop délicat en
  // pratique. Ce Stage ne fait donc plus que l'afficher ; seule la box entière
  // reste manipulable, via la poignée générique du canvas (cf. Resizer.jsx).
  // Textes posés par-dessus le canvas (cf. ShapeTextOverlay), à la même
  // échelle que les sous-formes pendant l'aperçu d'un redimensionnement.
  const { scaleX: designScaleX, scaleY: designScaleY } = getCompoundDesignScale(box);
  // Redimensionner le Stage efface son canvas, et react-konva ne le redessine
  // qu'à la frame suivante (batchDraw) : pendant un resize, la forme
  // s'affichait vide ou en retard sur le contour de la box, qui, lui, suit le
  // curseur — d'où un tremblement. Dessin immédiat, avant que la frame ne
  // s'affiche.
  const stageWidth = box.width + canvasMargin * 2;
  const stageHeight = box.height + canvasMargin * 2;
  useLayoutEffect(() => {
    stageRef.current?.draw();
  }, [stageWidth, stageHeight]);
  const stage = (
    <div style={{ position: 'relative' }}>
      <Stage ref={stageRef} width={stageWidth} height={stageHeight}>
        <Layer>
          {getShapeComponent(box, undefined, canvasMargin, {})}
        </Layer>
      </Stage>
      {isCompound && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            transform: `scale(${designScaleX}, ${designScaleY})`,
            transformOrigin: `${canvasMargin}px ${canvasMargin}px`,
          }}
        >
          <ShapeTextOverlay box={box} margin={canvasMargin} />
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Double-clic = raccourci direct vers l'édition plein écran (seul
          endroit où une sous-forme reste éditable) — pertinent uniquement
          pour une forme composée, une forme simple s'édite déjà en place. */}
      <div onDoubleClick={isCompound ? () => setIsEditorOpen(true) : undefined}>
        {canvasMargin > 0 ? (
          <div style={{ position: 'relative', width: box.width, height: box.height }}>
            <div
              style={{
                position: 'absolute',
                top: -canvasMargin,
                left: -canvasMargin,
                // Passe-clic vers la box en dessous (cf. shapeUtils) : ce
                // Stage n'a plus aucun contenu interactif à recevoir.
                pointerEvents: 'none',
              }}
            >
              {stage}
            </div>
          </div>
        ) : stage}
      </div>
      {isSelected && (
        <>
          <BoxFormControlsPortal title={t('shape')}>
            {/* Une ligne par réglage, libellé à gauche : les deux pastilles de
                couleur et les champs numériques étaient posés côte à côte sans
                un mot, et rien ne disait lequel réglait quoi. Couleur de fond/
                bordure (BackgroundColorPicker/BorderManager, forme simple) et
                leur équivalent par sous-forme vivent dans ShapeComponent, à la
                place demandée dans l'agencement du panneau. */}
            {/* Absent des formes déjà figées par nature (carré, rond...) :
                les déverrouiller reviendrait à en changer le type, ce que fait
                déjà le sélecteur juste en dessous. */}
            {canLockShapeRatio(box) && (
              <LabelledRow label={t('lock-ratio')}>
                <Switch
                  size="small"
                  checked={!!box.lockRatio}
                  onChange={(checked) => updateBox(box.id, (draft) => {
                    draft.lockRatio = checked;
                  })}
                />
              </LabelledRow>
            )}
            <ShapeComponent
              selectedBox={box}
              isCompound={isCompound}
              subShapes={subShapes}
              onOpenFullscreenEditor={openEditor}
              onGenerateThumbnail={handleGenerateThumbnail}
            />
          </BoxFormControlsPortal>
          <BoxActionsPanel />
        </>
      )}
      {isCompound && isEditorOpen && (
        <ShapeFullscreenEditor box={box} open={isEditorOpen} onClose={closeEditor} />
      )}
    </>
  );
};

// Position (en %) de chaque coin dans l'aperçu du panneau « Par angle » —
// carré/rectangle : coins réels d'un Rect axis-aligned, dans l'ordre de
// cornerLabel (haut-gauche, haut-droite, bas-droite, bas-gauche). Triangle/
// hexagone : sommets d'un polygone régulier, sommet 0 en haut, sens horaire —
// même convention que Konva.RegularPolygon (pas de rotation appliquée).
// Aperçu : la forme tient dans un carré de CORNER_PREVIEW_SIZE (les polygones y
// restent carrés ; un rectangle garde son ratio, borné), centrée dans la zone.
// Les champs se posent tout autour, hors de sa boîte, à CORNER_INPUT_GAP d'elle
// (au-dessus ou en dessous pour les coins/sommets proches de l'axe vertical,
// sur les côtés pour les sommets latéraux).
const CORNER_PREVIEW_SIZE = 72;
const CORNER_PREVIEW_MAX_RATIO = 1.8;
const CORNER_PREVIEW_MIN_RATIO = 0.5;
const CORNER_INPUT_WIDTH = 60;
const CORNER_INPUT_HEIGHT = 32;
const CORNER_INPUT_GAP = 6;
const CORNER_PREVIEW_WIDTH = CORNER_PREVIEW_SIZE + 2 * (CORNER_INPUT_WIDTH + CORNER_INPUT_GAP);
const CORNER_PREVIEW_HEIGHT = CORNER_PREVIEW_SIZE + 2 * (CORNER_INPUT_HEIGHT + CORNER_INPUT_GAP);
const cornerPreviewRect = (cornerCount, width, height) => {
  const ratio = cornerCount === 4 && width > 0 && height > 0
    ? Math.min(CORNER_PREVIEW_MAX_RATIO, Math.max(CORNER_PREVIEW_MIN_RATIO, width / height))
    : 1;
  const w = CORNER_PREVIEW_SIZE * ratio;
  const h = CORNER_PREVIEW_SIZE;
  return {
    left: (CORNER_PREVIEW_WIDTH - w) / 2,
    top: CORNER_INPUT_HEIGHT + CORNER_INPUT_GAP,
    width: w,
    height: h,
  };
};
const cornerInputPosition = (p, cornerCount, rect) => {
  const px = rect.left + (p.x / 100) * rect.width;
  const py = rect.top + (p.y / 100) * rect.height;
  if (cornerCount === 4 || Math.abs(p.x - 50) <= 30) {
    return {
      left: px,
      top: p.y < 50
        ? rect.top - CORNER_INPUT_GAP - CORNER_INPUT_HEIGHT / 2
        : rect.top + rect.height + CORNER_INPUT_GAP + CORNER_INPUT_HEIGHT / 2,
    };
  }
  return {
    left: p.x < 50
      ? rect.left - CORNER_INPUT_GAP - CORNER_INPUT_WIDTH / 2
      : rect.left + rect.width + CORNER_INPUT_GAP + CORNER_INPUT_WIDTH / 2,
    top: py,
  };
};
// Tracé SVG d'un polygone dont chaque sommet est arrondi (courbe quadratique
// dont le point de contrôle est le sommet) : `ratios[i]` (0..1) est la part de
// la demi-arête adjacente la plus courte consommée par l'arrondi du sommet i.
const roundedPolygonPath = (points, ratios) => {
  const n = points.length;
  const toward = (from, to, distance) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    return { x: from.x + (dx / length) * distance, y: from.y + (dy / length) * distance };
  };
  return `${points.map((vertex, i) => {
    const prev = points[(i + n - 1) % n];
    const next = points[(i + 1) % n];
    const radius = Math.min(1, Math.max(0, ratios[i] ?? 0))
      * Math.min(Math.hypot(prev.x - vertex.x, prev.y - vertex.y), Math.hypot(next.x - vertex.x, next.y - vertex.y)) / 2;
    const start = toward(vertex, prev, radius);
    const end = toward(vertex, next, radius);
    return `${i === 0 ? 'M' : 'L'}${start.x},${start.y} Q${vertex.x},${vertex.y} ${end.x},${end.y}`;
  }).join(' ')} Z`;
};
const cornerPreviewPositions = (cornerCount) => {
  if (cornerCount === 4) {
    return [{ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 }];
  }
  return Array.from({ length: cornerCount }, (_, index) => {
    const angle = (-90 + (360 / cornerCount) * index) * (Math.PI / 180);
    return { x: 50 + 50 * Math.cos(angle), y: 50 + 50 * Math.sin(angle) };
  });
};

// Sous-titre de section à l'intérieur du panneau « Forme » d'une forme
// composée en édition riche (cf. ShapeFullscreenEditor) : ce panneau est
// rendu hors de BoxFormControlsPortal (pas de portail vers l'inspecteur de
// droite, cf. plus bas), donc sans le titre "FORME" que ce portail donne
// gratuitement ailleurs — même look (uppercase, souligné, couleur d'accent
// par libellé) que ses titres de section, reconstitué ici à la main.
export const ShapePanelSeparator = ({ title, color = sectionAccentColor(title) }) => (
  <div style={{
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    display: 'inline-block',
    color,
    borderBottom: `2px solid ${color}`,
    paddingBottom: 4,
    marginTop: 8,
    marginBottom: 4,
  }}>
    {title}
  </div>
);

// Sentinelle du sélecteur Type hors modale, quand la box est déjà composée
// (cf. ShapeComponent#richEditing) : sa seule valeur possible tant qu'on ne
// choisit rien d'autre — ce n'est pas une vraie forme sélectionnable dans la
// liste, juste ce qui reflète l'état courant.
const COMPOUND_TYPE_VALUE = '__compound__';

// Largeur des champs numériques du panneau : celle du sélecteur Type
// (NavbarSelector, 120px par défaut) juste au-dessus, pour des contrôles alignés.
const PANEL_INPUT_WIDTH = 120;

// `richEditing` distingue les deux contextes où ce panneau s'affiche : à
// l'intérieur de l'éditeur plein écran (cf. ShapeFullscreenEditor), où une
// sous-forme précise reste sélectionnable et tous ses réglages restent
// modifiables un par un — et directement sur le canevas principal, où ce
// n'est plus le cas (cf. Shape.jsx#Shape) : seuls le type global, la taille
// de la box et un bouton pour rejoindre la modale y ont encore un sens.
export const ShapeComponent = ({ selectedBox, isCompound = false, subShapes = [], activeSubShapeId = null, onSelectSubShapeId, onEditSubShapeText, onOpenFullscreenEditor, onGenerateThumbnail, hideLibrarySave = false, richEditing = false }) => {
  const boxId = selectedBox?.id;
  const { t } = useTranslation();
  const { updateBox, configuration, currentDocument, currentProposal } = useStore(selector(boxId));
  const [libraryShapeName, setLibraryShapeName] = useState('');
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  // Dossiers admin de la banque, destinations possibles à l'enregistrement ;
  // sans choix (undefined), la forme va dans le dossier auto du document.
  const shapeBankVersion = useStore((state) => state.shapeBankVersion);
  const [targetFolders, setTargetFolders] = useState([]);
  const [targetFolderId, setTargetFolderId] = useState(undefined);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  // Une forme "propre au modèle" va dans le dossier auto du document, privé à
  // celui-ci (cf. ShapeBankServing) ; sinon dans un dossier admin partagé.
  const [onlyThisDocument, setOnlyThisDocument] = useState(false);
  // Une opportunité/offre n'a pas de route de création propre (cf.
  // ShapeBankServing) : elle passe par le Proposal qui la porte, dont l'id
  // diffère du sien — même distinction que restoreContentVersion/
  // savePagePreview (cf. src/store/api.js, src/hooks.js). Sur un Proposal, la
  // forme est toujours propre au document.
  const isProposalScoped = currentDocument.type === 'opportunity' || currentDocument.type === 'offer';
  // Deux <input type="file"> cachés, plutôt qu'un seul réutilisé : importer
  // une nouvelle sous-forme et remplacer l'import de la sous-forme active sont
  // deux gestes distincts (cf. pendingSvgImport, applySvgAsShape/AsImage),
  // déclenchés depuis deux endroits différents du panneau.
  const importSvgInputRef = useRef(null);
  const replaceSvgInputRef = useRef(null);

  const currentSubShape = isCompound ? subShapes.find((s) => s.id === activeSubShapeId) : null;

  // Champs à plat, indifférents à la légende forme unique / sous-forme : les
  // handlers ci-dessous restent identiques dans les deux cas, seule la cible
  // mutée par updateTarget change.
  const targetShapeKind = isCompound ? currentSubShape?.shape : selectedBox?.content?.shape;
  const targetWidth = isCompound ? currentSubShape?.width : selectedBox?.width;
  const targetHeight = isCompound ? currentSubShape?.height : selectedBox?.height;
  const targetBorders = isCompound ? currentSubShape?.borders : selectedBox?.borders;
  const targetRadius = isCompound ? currentSubShape?.radius : selectedBox?.radius;
  const targetCornerRadii = isCompound ? currentSubShape?.cornerRadii : selectedBox?.cornerRadii;
  const targetShadow = isCompound ? currentSubShape?.shadow : selectedBox?.shadow;
  const targetArrows = isCompound ? currentSubShape?.arrows : selectedBox?.arrows;
  const targetStroke = isCompound ? currentSubShape?.stroke : selectedBox?.stroke;
  const targetColor = isCompound ? currentSubShape?.color : selectedBox?.content?.color;
  // Position/rotation d'une sous-forme précise : seule l'édition riche (une
  // sous-forme active) les expose ici. La rotation du conteneur (forme simple ou
  // box composée hors modale) n'a plus de champ dans ce panneau.
  const targetX = currentSubShape?.x;
  const targetY = currentSubShape?.y;
  const targetRotation = currentSubShape?.rotation;

  // Applique une mutation à la forme unique (la box directement) ou à la
  // sous-forme actuellement sélectionnée d'une forme composée — mêmes
  // handlers dans les deux cas, seule la cible change.
  const updateTarget = (mutator) => {
    updateBox(boxId, (draft) => {
      if (isCompound) {
        const sub = draft.content.shapes.find((s) => s.id === activeSubShapeId) ?? draft.content.shapes[0];
        if (sub) mutator(sub);
      } else {
        mutator(draft);
      }
    });
  };

  const isAnArrow = targetShapeKind === 'arrow';
  const isSvgSubShape = targetShapeKind === 'svg' || targetShapeKind === 'svg-image';
  const isRadiusCapable = RADIUS_CAPABLE_SHAPES.includes(targetShapeKind);
  const cornerCount = CORNER_COUNT_BY_SHAPE[targetShapeKind] ?? 0;
const cornerRect = cornerPreviewRect(cornerCount, targetWidth, targetHeight);
  const [cornerPanelKeys, setCornerPanelKeys] = useState([]);
  // Rectangle/carré : Konva arrondit un `Rect` jusqu'à moitié de son plus petit
  // côté. Triangle/hexagone : passé la moitié du rayon du polygone, l'arrondi
  // Konva plafonne déjà (cercle parfait) — cf. getPolygonRadius.
  const polygonRadius = targetShapeKind ? getPolygonRadius(targetShapeKind, targetWidth, targetHeight, targetBorders?.size ?? 0) : null;
  const maxRadius = polygonRadius != null
    ? Math.floor(polygonRadius / 2)
    : Math.floor(Math.min(targetWidth ?? 0, targetHeight ?? 0) / 2);

  // N'est plus déclenché que depuis la ligne "Formes" en édition riche (cf.
  // plus bas) : une forme simple bascule désormais en composée via le bouton
  // "Édition" (handleEnterShapeEditing), pas en ajoutant directement une
  // deuxième sous-forme.
  const handleAddShape = () => {
    const newId = uuid();
    const defaultColor = configuration.shapeColor.style;
    updateBox(boxId, (draft) => {
      mutateAddSubShape(draft, newId, defaultColor);
      delete draft.content.libraryShapeName;
    });
    onSelectSubShapeId?.(newId);
  };

  // Bascule une forme simple en forme composée à une seule sous-forme —
  // elle-même, sans en ajouter une deuxième par défaut (cf.
  // mutateMakeCompoundWithSingleSubShape) — puis ouvre directement l'éditeur
  // plein écran : Ajouter/Importer un SVG, avant montrés ici, restent
  // disponibles une fois dedans (cf. la ligne "Formes" en édition riche).
  const handleEnterShapeEditing = () => {
    const newId = uuid();
    updateBox(boxId, (draft) => mutateMakeCompoundWithSingleSubShape(draft, newId));
    onSelectSubShapeId?.(newId);
    onOpenFullscreenEditor?.(newId);
  };

  // Un fichier .svg peut s'importer de deux façons irréconciliables (cf.
  // pendingSvgImport) : « Forme » (un seul chemin recolorable, mais qui perd
  // les couleurs et détails d'origine) ou « Image » (couleurs d'origine
  // intactes, mais plus recolorable/éditable comme une forme). Le fichier
  // choisi attend ce choix avant d'être traité par applySvgAsShape/AsImage.
  const [pendingSvgImport, setPendingSvgImport] = useState(null);

  const insertOrReplaceSubShape = (payload) => {
    const { mode } = pendingSvgImport;
    if (mode === 'replace') {
      updateTarget((sub) => mutateReplaceSubShapeImport(sub, payload));
    } else {
      const newId = uuid();
      updateBox(boxId, (draft) => {
        mutateAddImportedSubShape(draft, newId, payload);
        delete draft.content.libraryShapeName;
      });
      onSelectSubShapeId?.(newId);
    }
    setPendingSvgImport(null);
  };

  const applySvgAsShape = async () => {
    const parsed = await parseSvgFile(await pendingSvgImport.file.text());
    if (!parsed) {
      message.error(t('svg-import-error'));
      setPendingSvgImport(null);
      return;
    }
    insertOrReplaceSubShape({ shape: 'svg', ...parsed, color: configuration.shapeColor.style });
  };

  // Le fichier lui-même devient une data URI (portable, cf. parseSvgViewBox) :
  // aucune couleur de forme à fournir, l'image garde les siennes.
  const applySvgAsImage = async () => {
    const text = await pendingSvgImport.file.text();
    const svgViewBox = parseSvgViewBox(text);
    if (!svgViewBox) {
      message.error(t('svg-import-error'));
      setPendingSvgImport(null);
      return;
    }
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`;
    insertOrReplaceSubShape({ shape: 'svg-image', svgDataUrl, svgViewBox });
  };

  const canSaveToLibrary = isCompound && !hideLibrarySave;
  useEffect(() => {
    if (!saveModalOpen || isProposalScoped || !currentDocument?.id) return undefined;
    let cancelled = false;
    getShapeBank(currentDocument).then((data) => {
      if (cancelled) return;
      const folders = (data ?? []).filter((folder) => folder.origin === 'admin');
      setTargetFolders(folders);
      setTargetFolderId((current) => (folders.some((f) => f.id === current) ? current : undefined));
    });
    return () => {
      cancelled = true;
    };
  }, [saveModalOpen, isProposalScoped, currentDocument, shapeBankVersion]);

  // Enregistre la forme composée courante (les sous-formes à plat, telles que
  // rejouées à l'insertion — cf. Proposals::Shape#normalize_definition côté
  // back) dans le dossier auto du document. La vignette vient du Stage
  // réellement affiché (cf. Shape.jsx/ShapeFullscreenEditor), pas d'un rendu
  // séparé.
  const openSaveModal = () => {
    setLibraryShapeName('');
    setTargetFolderId(undefined);
    setOnlyThisDocument(false);
    setSaveModalOpen(true);
  };

  const handleSaveToLibrary = async () => {
    const name = libraryShapeName.trim();
    if (!name || !isCompound || savingToLibrary) return;
    const ownFolder = isProposalScoped || onlyThisDocument;
    if (!ownFolder && !targetFolderId) return;
    const documentId = isProposalScoped ? currentProposal?.id : currentDocument.id;
    if (!documentId) return;
    setSavingToLibrary(true);
    try {
      const dataUrl = onGenerateThumbnail?.();
      const thumbnailContent = dataUrl?.includes(',') ? dataUrl.split(',')[1] : undefined;
      await postShapeBankItem(documentId, currentDocument.type, {
        name,
        width: selectedBox.width,
        height: selectedBox.height,
        // Les textes partent en JSON brut : un EditorState ne se sérialise pas.
        definition: serializeShapeTexts(subShapes),
        thumbnailContent,
        folderId: ownFolder ? undefined : targetFolderId,
      });
      useStore.getState().bumpShapeBank();
      setLibraryShapeName('');
      setSaveModalOpen(false);
      message.success(t('shape-saved-to-library'));
    } catch (error) {
      message.error(t('shape-save-error'));
    } finally {
      setSavingToLibrary(false);
    }
  };

  // Remplace le contenu de la box par la forme choisie dans la bibliothèque —
  // même geste qu'appliquer un média de la banque sur une box image (cf.
  // Image.jsx#applyBankMedia). Des ids neufs évitent une collision si la même
  // forme de la banque est insérée plusieurs fois dans le document ; les liens
  // suivent leurs extrémités (cf. remapSubShapeIds), les textes retrouvent leur
  // EditorState.
  const handleInsertShapeFromLibrary = (shape) => {
    updateBox(boxId, (draft) => {
      draft.width = shape.width;
      draft.height = shape.height;
      draft.content = draft.content ?? {};
      draft.content.shapes = hydrateShapeTexts(
        remapSubShapeIds(shape.definition ?? [], uuid),
        (raw) => EditorState.createWithContent(convertFromRaw(raw), decorator)
      );
      draft.content.libraryShapeName = shape.name;
    });
  };

  // Bloc texte libre, aussitôt ouvert à la saisie.
  const handleAddTextBlock = () => {
    const newId = uuid();
    updateBox(boxId, (draft) => {
      mutateAddTextSubShape(draft, newId);
      delete draft.content.libraryShapeName;
    });
    onSelectSubShapeId?.(newId);
    onEditSubShapeText?.(newId);
  };

  const handleDuplicateSubShape = () => {
    if (!activeSubShapeId) return;
    const newId = uuid();
    updateBox(boxId, (draft) => {
      mutateDuplicateSubShape(draft, activeSubShapeId, newId);
      delete draft.content.libraryShapeName;
    });
    onSelectSubShapeId?.(newId);
  };

  const handleRemoveSubShape = () => {
    if (!activeSubShapeId || subShapes.length <= 1) return;
    const remaining = subShapes.filter((s) => s.id !== activeSubShapeId);
    updateBox(boxId, (draft) => {
      mutateRemoveSubShape(draft, activeSubShapeId);
      delete draft.content.libraryShapeName;
    });
    onSelectSubShapeId?.(remaining[0]?.id ?? null);
  };

  const handleSelectSubShapeKind = (shapeId) => {
    updateTarget((sub) => mutateSetSubShapeKind(sub, shapeId));
  };

  // Sélecteur Type unifié (hors modale, cf. richEditing) : sur une forme
  // simple, change son type comme avant (cf. ShapeKindSelector) ; sur une
  // forme composée, la seule valeur affichée est COMPOUND_TYPE_VALUE (son nom
  // de bibliothèque ou « Personnalisé ») — la reproposer dans la liste ne fait
  // rien, choisir une forme de base l'effondre en forme simple de ce type.
  const handleSelectUnifiedKind = (shapeId) => {
    if (shapeId === COMPOUND_TYPE_VALUE) return;
    const defaultColor = configuration.shapeColor.style;
    updateBox(boxId, (draft) => mutateCollapseCompoundToShapeKind(draft, shapeId, defaultColor));
  };

  const handleReorderSubShape = (direction) => {
    if (!activeSubShapeId) return;
    updateBox(boxId, (draft) => mutateReorderSubShape(draft, activeSubShapeId, direction));
  };

  const handleChangeSubShapeGeometry = (field) => (value) => {
    updateTarget((sub) => {
      sub[field] = value;
    });
  };

  // Largeur/hauteur d'une sous-forme : l'autre suit pour une forme à ratio
  // (cf. mutateSetSubShapeSize).
  const handleChangeSubShapeSize = (field) => (value) => {
    updateTarget((sub) => mutateSetSubShapeSize(sub, field, value));
  };

  // Le conteneur lui-même. Dans l'éditeur plein écran (richEditing), largeur et
  // hauteur sont libres : on y édite déjà les sous-formes, autant pouvoir
  // déformer le conteneur. Hors modale, comme la poignée générique du canvas
  // (ratio verrouillé pour une forme composée, cf. Resizer.jsx), l'autre
  // dimension suit pour garder le ratio. Même mutateur que le resize générique
  // (mutateRescaleSubShapes), juste déclenché par une valeur saisie plutôt
  // qu'un glissement.
  const handleChangeContainerSize = (field) => (value) => {
    updateBox(boxId, (draft) => {
      const oldWidth = draft.width;
      const oldHeight = draft.height;
      draft[field] = value;
      if (!richEditing && oldWidth > 0 && oldHeight > 0) {
        if (field === 'width') draft.height = value * (oldHeight / oldWidth);
        else draft.width = value * (oldWidth / oldHeight);
      }
      mutateRescaleSubShapes(draft, oldWidth, oldHeight);
    });
  };

  // Aligne la sous-forme sur les bords/centre de la box elle-même (le
  // « canevas » de la forme composée) — pas de dépendance à computeSnap, qui
  // ne joue que pendant un glissement.
  const handleAlignSubShapeToCanvas = (side) => {
    updateTarget((sub) => {
      if (side === 'left') sub.x = 0;
      else if (side === 'right') sub.x = selectedBox.width - sub.width;
      else if (side === 'horizontal-center') sub.x = (selectedBox.width - sub.width) / 2;
      else if (side === 'top') sub.y = 0;
      else if (side === 'bottom') sub.y = selectedBox.height - sub.height;
      else if (side === 'vertical-center') sub.y = (selectedBox.height - sub.height) / 2;
    });
  };

  const handleChangeSubShapeColor = (label) => {
    updateTarget((t) => {
      t.color = configuration.colors.find((color) => color.label === label)?.style;
    });
  };

  const handleChangeSubShapeBorderColor = (label) => {
    updateTarget((t) => {
      t.borders.color = configuration.colors.find((color) => color.label === label);
    });
  };

  const handleArrowsChangement = (place) => {
    if (
      targetArrows[place] &&
      ((place === 'left' && targetArrows.right === false) ||
        (place === 'right' && targetArrows.left === false))
    ) {
      return;
    }
    updateTarget((t) => {
      t.arrows[place] = !t.arrows[place];
    });
  };

  const handleChangeBordersSize = (value) => {
    updateTarget((t) => {
      t.borders.size = value;
    });
  };

  const handleChangeRadius = (value) => {
    updateTarget((t) => {
      t.radius = value;
    });
  };

  // Ouvrir le panneau « Par angle » amorce cornerRadii depuis le rayon uniforme
  // actuel : sans ça la forme sauterait au premier champ modifié (les angles
  // pas encore touchés valant 0 par défaut).
  const handleTogglePerCorner = (keys) => {
    setCornerPanelKeys(keys);
    if (keys.length > 0 && !targetCornerRadii) {
      updateTarget((t) => {
        t.cornerRadii = Array(cornerCount).fill(t.radius ?? 0);
      });
    }
  };

  // `cornerRadii` n'est posé qu'à l'ouverture du panneau « Par angle » : si le
  // panneau reste ouvert quand la sélection change (autre sous-forme, autre
  // box), la nouvelle cible n'en a pas encore — on l'initialise ici aussi.
  const handleChangeCornerRadius = (index, value) => {
    updateTarget((t) => {
      t.cornerRadii = t.cornerRadii ?? Array(cornerCount).fill(t.radius ?? 0);
      t.cornerRadii[index] = value;
    });
  };

  const handleResetCornerRadii = () => {
    updateTarget((t) => {
      delete t.cornerRadii;
    });
    setCornerPanelKeys([]);
  };

  const handleToggleShadow = (enabled) => {
    updateTarget((t) => {
      t.shadow.enabled = enabled;
    });
  };

  const handleChangeShadowColor = (label) => {
    updateTarget((t) => {
      t.shadow.color = configuration.colors.find((color) => color.label === label);
    });
  };

  const handleChangeShadowField = (field) => (value) => {
    updateTarget((t) => {
      t.shadow[field] = value;
    });
  };

  const cornerLabel = (index) => {
    if (cornerCount === 4) {
      return [
        t('corner-top-left'),
        t('corner-top-right'),
        t('corner-bottom-right'),
        t('corner-bottom-left'),
      ][index];
    }
    return `${t('corner')} ${index + 1}`;
  };

  // Rotation de la sous-forme active, elle, fait partie de sa position/
  // taille précise (cf. positionSizeRotationBlock) — seule l'édition riche
  // l'affiche.
  const subShapeRotationRow = (
    <LabelledRow label={t('rotation')}>
      <NavbarInputNumber
        width={PANEL_INPUT_WIDTH}
        min={-180}
        max={180}
        tooltipContent={t('rotation')}
        value={targetRotation}
        onChange={handleChangeSubShapeGeometry('rotation')}
        defaultValue={0}
      />
    </LabelledRow>
  );

  const shadowSection = (
    <>
      <LabelledRow label={t('shadow')}>
        <Switch checked={!!targetShadow?.enabled} onChange={handleToggleShadow} />
      </LabelledRow>
      {targetShadow?.enabled && (
        <>
          <LabelledRow label={t('shadow-color')}>
            <NavbarListButton
              tooltipContent={t('shadow-color')}
              colorInIcon
              icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle(true)} />}
              onListElementClick={handleChangeShadowColor}
              listElements={filterSameColor(configuration.colors.filter((c) => c.label !== 'transparent'))}
              selectedElement={targetShadow?.color}
            />
          </LabelledRow>
          <LabelledRow label={t('shadow-blur')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={0}
              max={50}
              tooltipContent={t('shadow-blur')}
              value={targetShadow?.blur}
              onChange={handleChangeShadowField('blur')}
              defaultValue={6}
            />
          </LabelledRow>
          <LabelledRow label={t('shadow-offset-x')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={-50}
              max={50}
              tooltipContent={t('shadow-offset-x')}
              value={targetShadow?.offsetX}
              onChange={handleChangeShadowField('offsetX')}
              defaultValue={4}
            />
          </LabelledRow>
          <LabelledRow label={t('shadow-offset-y')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={-50}
              max={50}
              tooltipContent={t('shadow-offset-y')}
              value={targetShadow?.offsetY}
              onChange={handleChangeShadowField('offsetY')}
              defaultValue={4}
            />
          </LabelledRow>
          <LabelledRow label={t('shadow-opacity')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={0}
              max={1}
              step={0.1}
              tooltipContent={t('shadow-opacity')}
              value={targetShadow?.opacity}
              onChange={handleChangeShadowField('opacity')}
              defaultValue={0.5}
            />
          </LabelledRow>
        </>
      )}
    </>
  );

  const positionSizeRotationBlock = (
    <>
      <LabelledRow label={t('position-x')}>
        <NavbarInputNumber
          width={PANEL_INPUT_WIDTH}
          tooltipContent={t('position-x')}
          value={targetX}
          onChange={handleChangeSubShapeGeometry('x')}
          defaultValue={0}
        />
      </LabelledRow>
      <LabelledRow label={t('position-y')}>
        <NavbarInputNumber
          width={PANEL_INPUT_WIDTH}
          tooltipContent={t('position-y')}
          value={targetY}
          onChange={handleChangeSubShapeGeometry('y')}
          defaultValue={0}
        />
      </LabelledRow>
      <LabelledRow label={t('height')}>
        <NavbarInputNumber
          width={PANEL_INPUT_WIDTH}
          min={10}
          tooltipContent={t('height')}
          value={targetHeight}
          onChange={handleChangeSubShapeSize('height')}
          defaultValue={10}
        />
      </LabelledRow>
      <LabelledRow label={t('width')}>
        <NavbarInputNumber
          width={PANEL_INPUT_WIDTH}
          min={10}
          tooltipContent={t('width')}
          value={targetWidth}
          onChange={handleChangeSubShapeSize('width')}
          defaultValue={10}
        />
      </LabelledRow>
      {subShapeRotationRow}
    </>
  );

  const canConfirmSave =
    !!libraryShapeName.trim() && !savingToLibrary && (isProposalScoped || onlyThisDocument || !!targetFolderId);

  const buttonAccent = getPrimaryDark(currentDocument.type === 'template');

  const librarySaveRow = (
    <>
      <Button
        className='style_btn_right_side'
        style={{ color: buttonAccent, borderColor: buttonAccent, '--btn-accent-color': buttonAccent, fontSize: 13, width: '100%', marginBottom: 10 }}
        icon={<DuotoneLightIcon name='floppy-disk' style={{ marginRight: 8 }} />}
        data-outside-ignore="true"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          openSaveModal();
        }}
        ghost
      >
        {t('save-shape')}
      </Button>
      <Modal
        open={saveModalOpen}
        title={t('save-shape')}
        onCancel={() => setSaveModalOpen(false)}
        destroyOnHidden
        modalRender={(node) => <div data-outside-ignore="true">{node}</div>}
        footer={[
          <Button key='cancel' danger onClick={() => setSaveModalOpen(false)}>
            {t('cancel')}
          </Button>,
          <Button key='save' type='primary' disabled={!canConfirmSave} loading={savingToLibrary} onClick={handleSaveToLibrary}>
            {t('save')}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <Input
            value={libraryShapeName}
            onChange={(e) => setLibraryShapeName(e.target.value)}
            placeholder={t('shape-name-placeholder')}
            autoFocus
          />
          {isProposalScoped ? (
            <span>{t('shape-only-this-document')}</span>
          ) : (
            <>
              <Select
                value={targetFolderId}
                onChange={setTargetFolderId}
                disabled={onlyThisDocument}
                placeholder={t('shape-target-folder')}
                options={targetFolders.map((folder) => ({ value: folder.id, label: folder.name }))}
              />
              <Checkbox
                checked={onlyThisDocument}
                onChange={(e) => {
                  setOnlyThisDocument(e.target.checked);
                  if (e.target.checked) setTargetFolderId(undefined);
                }}
              >
                {t('shape-only-this-template')}
              </Checkbox>
            </>
          )}
        </div>
      </Modal>
    </>
  );

  return (
    <>
      {/* Taille du conteneur : libre dans l'éditeur plein écran (on y édite les
          sous-formes, autant pouvoir déformer la box), au ratio verrouillé hors
          modale comme sa poignée générique sur le canvas (cf.
          handleChangeContainerSize et Resizer.jsx). Le sous-titre "Canevas
          incluant les formes" ne sert que dans l'éditeur plein écran (cf.
          richEditing) : hors modale, ce bloc reste dans le panneau "FORME" de
          l'inspecteur de droite (cf. BoxFormControlsPortal), qui a déjà son
          propre titre. */}
      {isCompound && richEditing && <ShapePanelSeparator title={t('canvas-with-shapes')} color={ZONE_COLOR} />}
      {isCompound && (
        <>
          <LabelledRow label={t('height')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={10}
              tooltipContent={t('height')}
              value={selectedBox?.height}
              onChange={handleChangeContainerSize('height')}
              defaultValue={10}
            />
          </LabelledRow>
          <LabelledRow label={t('width')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={10}
              tooltipContent={t('width')}
              value={selectedBox?.width}
              onChange={handleChangeContainerSize('width')}
              defaultValue={10}
            />
          </LabelledRow>
        </>
      )}

      {isCompound && richEditing && <ShapePanelSeparator title={t('shapes')} color={SUB_SHAPE_COLOR} />}

      {/* Même ligne libellée que les autres réglages : le sélecteur posé seul
          au milieu du panneau n'annonçait pas ce qu'il choisissait. « Ligne » y
          figure aussi — l'élément « Ligne » du panneau de gauche a disparu, une
          ligne est une forme comme une autre. Une sous-forme n'a pas cette
          option : elle ne peut pas devenir un type de box différent. */}
      {!isCompound && <ShapeKindSelector box={selectedBox} />}
      {/* Hors modale, une forme composée n'affiche plus le type de LA
          sous-forme active (on ne peut plus en choisir une précise sans
          rouvrir la modale) mais celui de la box entière : son nom de
          bibliothèque si elle en vient, sinon « Personnalisé ». Le
          reproposer dans la liste ne fait rien (cf. handleSelectUnifiedKind)
          — choisir une forme de base l'effondre en forme simple. */}
      {!richEditing && isCompound && (
        <LabelledRow label={t('type')}>
          <NavbarSelector
            key='compoundTypeSelector'
            suffixIcon={<DuotoneLightIcon name='vector-polygon' />}
            onSelect={handleSelectUnifiedKind}
            selectedValue={COMPOUND_TYPE_VALUE}
            // Ni l'infobulle antd (pas de tooltipContent) ni le title natif que
            // rc-select pose sur la valeur choisie (le libellé, faute de title) :
            // un title vide l'évite.
            options={[
              { value: COMPOUND_TYPE_VALUE, label: selectedBox?.content?.libraryShapeName || t('custom-shape'), title: '' },
              ...SHAPES.filter((s) => s.id !== SHAPE_KIND_LINE).map(({ id, name }) => ({ label: t(name), value: id, title: '' })),
            ]}
          />
        </LabelledRow>
      )}
      {/* Un SVG importé (forme ou image) n'est pas une option du sélecteur
          (rien à choisir tant qu'aucun fichier n'a été déposé, cf. le bouton
          dédié plus haut) : sa ligne "Type" propose de le remplacer plutôt
          que de changer de forme. */}
      {richEditing && isCompound && isSvgSubShape && (
        <LabelledRow label={t('type')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'rgba(11,34,57,0.65)' }}>
              {targetShapeKind === 'svg' ? t('svg-imported') : t('svg-image-imported')}
            </span>
            <NavbarButton
              tooltipContent={t('replace-svg')}
              onClick={() => replaceSvgInputRef.current?.click()}
              icon={<DuotoneLightIcon name='file-svg' style={iconButtonStyle(true)} />}
            />
          </div>
          <input
            ref={replaceSvgInputRef}
            type='file'
            accept='.svg,image/svg+xml'
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) setPendingSvgImport({ file, mode: 'replace' });
            }}
          />
        </LabelledRow>
      )}
      {richEditing && isCompound && !isSvgSubShape && targetShapeKind !== TEXT_SUB_SHAPE && (
        <LabelledRow label={t('type')}>
          <NavbarSelector
            key='subShapeTypeSelector'
            tooltipContent={t('shape')}
            suffixIcon={<DuotoneLightIcon name='vector-polygon' />}
            onSelect={handleSelectSubShapeKind}
            selectedValue={targetShapeKind}
            options={SHAPES.filter((s) => s.id !== SHAPE_KIND_LINE).map(({ id, name }) => ({ label: t(name), value: id }))}
          />
        </LabelledRow>
      )}

      {/* Bande des sous-formes : hors édition riche (forme simple ou box
          composée pas encore ouverte en grand), un seul bouton a un sens —
          "Édition", qui (re)bascule sur l'éditeur plein écran — plutôt que le
          libellé "Formes" empilé, pensé pour la liste de boutons ajout/
          import/dupliquer/supprimer/réordre qui n'a de sens qu'à l'intérieur
          de la modale (sous-forme active choisie, au moins deux sous-formes
          possibles). Le sélecteur de sous-forme y est un dropdown (pas un
          bouton par sous-forme) : au-delà de deux ou trois, les boutons
          d'action étaient poussés hors du panneau par la liste qui grandit
          d'autant. */}
      {!richEditing ? (
        <LabelledRow label={t('shapes-edit-row-label')}>
          <NavbarButton
            tooltipContent={t('open-fullscreen-editor')}
            onClick={isCompound ? () => onOpenFullscreenEditor?.() : handleEnterShapeEditing}
            icon={<ShapesStarEditIcon style={iconButtonStyle(true)} />}
          />
        </LabelledRow>
      ) : (
        <LabelledRow label={t('shapes')} stacked>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <NavbarSelector
              key='subShapeSelector'
              width={64}
              tooltipContent={t('shape')}
              onSelect={(value) => onSelectSubShapeId?.(value)}
              selectedValue={activeSubShapeId}
              searchable={false}
              options={subShapes.map((sub, index) => ({ value: sub.id, label: `${index + 1}` }))}
            />
            <NavbarButton
              tooltipContent={t('add-shape')}
              onClick={handleAddShape}
              icon={<DuotoneLightIcon name='plus' weight='solid' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('add-text-block')}
              onClick={handleAddTextBlock}
              icon={<DuotoneLightIcon name='text' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('import-svg')}
              onClick={() => importSvgInputRef.current?.click()}
              icon={<DuotoneLightIcon name='file-svg' style={iconButtonStyle(true)} />}
            />
            <input
              ref={importSvgInputRef}
              type='file'
              accept='.svg,image/svg+xml'
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) setPendingSvgImport({ file, mode: 'add' });
              }}
            />
            <NavbarButton
              tooltipContent={t('duplicate-shape')}
              onClick={handleDuplicateSubShape}
              icon={<DuotoneLightIcon name='clone' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('delete-shape')}
              disabled={subShapes.length <= 1}
              onClick={handleRemoveSubShape}
              icon={(
                <DuotoneLightIcon
                  name='trash'
                  className='box-delete-icon'
                  style={{ ...iconButtonStyle(), color: subShapes.length > 1 ? '#FF0000' : 'black' }}
                />
              )}
            />
            <NavbarButton
              tooltipContent={t('move-shape-backward')}
              disabled={subShapes.findIndex((s) => s.id === activeSubShapeId) <= 0}
              onClick={() => handleReorderSubShape(-1)}
              icon={<DuotoneLightIcon name='layer-minus' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('move-shape-forward')}
              disabled={subShapes.findIndex((s) => s.id === activeSubShapeId) >= subShapes.length - 1}
              onClick={() => handleReorderSubShape(1)}
              icon={<DuotoneLightIcon name='layer-plus' style={iconButtonStyle(true)} />}
            />
          </div>
        </LabelledRow>
      )}

      {/* Texte de la sous-forme active (double-clic sur la forme pour le
          saisir) : sa place dans le cadre. */}
      {richEditing && isCompound && currentSubShape && (
        <ShapePanelSections
          groupKey='shape-sub-text'
          defaultOpenKeys={['text']}
          sections={[{
            key: 'text',
            title: t('shape-text'),
            children: (
              <>
                <LabelledRow label={t('edit-shape-text')}>
                  <NavbarButton
                    tooltipContent={t('edit-shape-text')}
                    onClick={() => onEditSubShapeText?.(currentSubShape.id)}
                    icon={<DuotoneLightIcon name='pen-line' style={iconButtonStyle(true)} />}
                  />
                </LabelledRow>
                <LabelledRow label={t('text-align')}>
                  {TEXT_ALIGNS.map((align) => (
                    <NavbarButton
                      key={align}
                      tooltipContent={t(`text-align-${align}`)}
                      onClick={() => updateTarget((sub) => {
                        sub.textAlign = align;
                      })}
                      icon={<DuotoneLightIcon name={`align-${align}`} style={iconButtonStyle(textLayoutOf(currentSubShape).align === align)} />}
                    />
                  ))}
                </LabelledRow>
                <LabelledRow label={t('text-vertical-align')}>
                  {TEXT_VERTICAL_ALIGNS.map((verticalAlign) => (
                    <NavbarButton
                      key={verticalAlign}
                      tooltipContent={t(`text-valign-${verticalAlign}`)}
                      onClick={() => updateTarget((sub) => {
                        sub.textVerticalAlign = verticalAlign;
                      })}
                      icon={(
                        <DuotoneLightIcon
                          name={{ top: 'objects-align-top', middle: 'objects-align-center-vertical', bottom: 'objects-align-bottom' }[verticalAlign]}
                          style={iconButtonStyle(textLayoutOf(currentSubShape).verticalAlign === verticalAlign)}
                        />
                      )}
                    />
                  ))}
                </LabelledRow>
              </>
            ),
          }]}
        />
      )}

      {/* Alignement sur le canevas de la sous-forme active : seule une
          sous-forme en édition riche en a besoin, cf. handleAlignSubShapeToCanvas
          plus haut — une forme simple se déplace via les poignées génériques
          de la box. */}
      {richEditing && isCompound && (
        <LabelledRow label={t('align-to-canvas')} stacked>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <NavbarButton
              tooltipContent={t('align-left')}
              onClick={() => handleAlignSubShapeToCanvas('left')}
              icon={<DuotoneLightIcon name='arrow-left-to-line' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('align-horizontal-center')}
              onClick={() => handleAlignSubShapeToCanvas('horizontal-center')}
              icon={<DuotoneLightIcon name='border-center-h' weight='solid' style={{ ...iconButtonStyle(true), transform: 'rotate(90deg)' }} />}
            />
            <NavbarButton
              tooltipContent={t('align-right')}
              onClick={() => handleAlignSubShapeToCanvas('right')}
              icon={<DuotoneLightIcon name='arrow-right-to-line' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('align-top')}
              onClick={() => handleAlignSubShapeToCanvas('top')}
              icon={<DuotoneLightIcon name='arrow-up-to-line' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('align-vertical-center')}
              onClick={() => handleAlignSubShapeToCanvas('vertical-center')}
              icon={<DuotoneLightIcon name='border-center-h' weight='solid' style={iconButtonStyle(true)} />}
            />
            <NavbarButton
              tooltipContent={t('align-bottom')}
              onClick={() => handleAlignSubShapeToCanvas('bottom')}
              icon={<DuotoneLightIcon name='arrow-down-to-line' style={iconButtonStyle(true)} />}
            />
          </div>
        </LabelledRow>
      )}

      {/* Couleur de fond / couleur de bordure : pour une forme simple, les
          contrôles génériques (BackgroundColorPicker/BorderManager, câblés sur
          la box sélectionnée entière) vivaient auparavant dans le wrapper
          Shape, avant même ce composant — remontés ici pour tenir leur place
          dans l'agencement demandé, identique à celle des mêmes réglages côté
          sous-forme juste en dessous. */}
      {!isCompound && (
        <>
          <LabelledRow label={t('background-color')}>
            <BackgroundColorPicker />
          </LabelledRow>
          <LabelledRow label={t("border's-color")}>
            <BorderManager />
          </LabelledRow>
        </>
      )}

      {/* Couleur/bordure d'une sous-forme : contrôles dédiés, indépendants de
          BackgroundColorPicker/BorderManager (génériques à tous les types de
          box, câblés sur la box sélectionnée entière — pas adaptés à une
          sous-forme). */}
      {richEditing && isCompound && (
        <>
          {/* Une image gardant ses couleurs d'origine (cf. isSvgSubShape),
              une couleur de fond n'aurait aucun effet visible : Konva ignore
              `fill` sur un nœud Image. */}
          {targetShapeKind !== 'svg-image' && (
            <LabelledRow label={t('background-color')}>
              <NavbarListButton
                tooltipContent={t('background-color')}
                colorInIcon
                icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle(true)} />}
                onListElementClick={handleChangeSubShapeColor}
                listElements={filterSameColor(configuration.colors.filter((c) => c.label !== 'transparent'))}
                selectedElement={configuration.colors.find((c) => c.style === targetColor)}
              />
            </LabelledRow>
          )}
          <LabelledRow label={t("border's-color")}>
            <NavbarListButton
              tooltipContent={t("border's-color")}
              icon={<DuotoneLightIcon name='border-outer' style={iconButtonStyle(true)} />}
              onListElementClick={handleChangeSubShapeBorderColor}
              listElements={filterSameColor(configuration.colors.filter((c) => c.label !== 'transparent'))}
              selectedElement={targetBorders?.color}
              colorInIcon='primary'
            />
          </LabelledRow>
        </>
      )}

      {/* Taille de bordure et rayon d'angle : juste après la couleur de
          bordure, avant l'ombre — même bloc et même position relative pour
          une forme simple et pour une sous-forme en édition riche. */}
      {!isAnArrow && (!isCompound || richEditing) && (
        <LabelledRow label={t("border's-size")}>
          <NavbarInputNumber
            width={PANEL_INPUT_WIDTH}
            min={0}
            max={30}
            tooltipContent={t("border's-size")}
            value={targetBorders?.size}
            onChange={handleChangeBordersSize}
            defaultValue={0}
          />
        </LabelledRow>
      )}

      {/* Rayon d'angle : seulement pour les formes à angles droits que Konva
          sait arrondir (cf. RADIUS_CAPABLE_SHAPES) — un rond ou une étoile n'a
          pas de coin à arrondir. */}
      {isRadiusCapable && (!isCompound || richEditing) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <LabelledRow label={t('radius')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={0}
              max={maxRadius}
              tooltipContent={t('radius')}
              value={targetRadius}
              onChange={handleChangeRadius}
              defaultValue={0}
            />
          </LabelledRow>
          <Collapse
            size='small'
            ghost
            activeKey={cornerPanelKeys}
            onChange={handleTogglePerCorner}
            items={[
              {
                key: 'per-corner',
                label: t('per-corner-radius'),
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Aperçu schématique de la forme (pas ses couleurs/proportions
                        réelles, juste sa géométrie) : chaque champ posé à son coin,
                        pour que son effet sur l'aperçu dise sans ambiguïté quel
                        angle il règle — plus fiable qu'un intitulé "Coin 2". */}
                    <div style={{ position: 'relative', width: CORNER_PREVIEW_WIDTH, height: CORNER_PREVIEW_HEIGHT, margin: '4px auto 8px' }}>
                      {cornerCount === 4 ? (
                        <div style={{
                          position: 'absolute',
                          left: cornerRect.left,
                          top: cornerRect.top,
                          width: cornerRect.width,
                          height: cornerRect.height,
                          background: 'rgba(11,34,57,0.08)',
                          border: '1px solid rgba(11,34,57,0.3)',
                          borderTopLeftRadius: Math.min(cornerRect.height / 2, ((targetCornerRadii?.[0] ?? targetRadius ?? 0) / (maxRadius || 1)) * (cornerRect.height / 2)),
                          borderTopRightRadius: Math.min(cornerRect.height / 2, ((targetCornerRadii?.[1] ?? targetRadius ?? 0) / (maxRadius || 1)) * (cornerRect.height / 2)),
                          borderBottomRightRadius: Math.min(cornerRect.height / 2, ((targetCornerRadii?.[2] ?? targetRadius ?? 0) / (maxRadius || 1)) * (cornerRect.height / 2)),
                          borderBottomLeftRadius: Math.min(cornerRect.height / 2, ((targetCornerRadii?.[3] ?? targetRadius ?? 0) / (maxRadius || 1)) * (cornerRect.height / 2)),
                        }}
                        />
                      ) : (
                        <svg
                          width={cornerRect.width}
                          height={cornerRect.height}
                          style={{ position: 'absolute', left: cornerRect.left, top: cornerRect.top, overflow: 'visible' }}
                        >
                          <path
                            d={roundedPolygonPath(
                              cornerPreviewPositions(cornerCount).map((p) => ({ x: (p.x / 100) * cornerRect.width, y: (p.y / 100) * cornerRect.height })),
                              cornerPreviewPositions(cornerCount).map((_, index) => (targetCornerRadii?.[index] ?? targetRadius ?? 0) / (maxRadius || 1)),
                            )}
                            fill='rgba(11,34,57,0.08)'
                            stroke='rgba(11,34,57,0.3)'
                            strokeWidth={1}
                          />
                        </svg>
                      )}
                      {cornerPreviewPositions(cornerCount).map((p, index) => (
                        <div
                          key={index}
                          style={{ position: 'absolute', ...cornerInputPosition(p, cornerCount, cornerRect), transform: 'translate(-50%, -50%)' }}
                        >
                          <NavbarInputNumber
                            width={CORNER_INPUT_WIDTH}
                            min={0}
                            max={maxRadius}
                            tooltipContent={cornerLabel(index)}
                            value={targetCornerRadii?.[index] ?? targetRadius ?? 0}
                            onChange={(v) => handleChangeCornerRadius(index, v)}
                            defaultValue={0}
                          />
                        </div>
                      ))}
                      <div style={{ position: 'absolute', left: CORNER_PREVIEW_WIDTH / 2, top: cornerRect.top + cornerRect.height / 2, transform: 'translate(-50%, -50%)' }}>
                        <NavbarButton
                          tooltipContent={t('radius-reset')}
                          disabled={!targetCornerRadii}
                          onClick={handleResetCornerRadii}
                          icon={<DuotoneLightIcon name='arrow-rotate-left' style={iconButtonStyle(true)} />}
                        />
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Réglages propres à la flèche : montrés seulement pour une flèche.
          Auparavant ils restaient là, grisés, pour toutes les autres formes —
          trois champs inertes qui ressemblaient à un affichage cassé. */}
      {isAnArrow && (!isCompound || richEditing) && (
        <>
          <LabelledRow label={t('arrows')}>
            <NavbarButton
              tooltipContent={t('left-carret')}
              onClick={() => handleArrowsChangement('left')}
              icon={<DuotoneLightIcon name='caret-left' style={iconButtonStyle(targetArrows['left'])} />}
            />
            <NavbarButton
              tooltipContent={t('right-carret')}
              onClick={() => handleArrowsChangement('right')}
              icon={<DuotoneLightIcon name='caret-right' style={iconButtonStyle(targetArrows['right'])} />}
            />
          </LabelledRow>
          <LabelledRow label={t('line-stroke')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={1}
              max={30}
              tooltipContent={t('line-stroke')}
              value={targetStroke}
              onChange={(v) =>
                updateTarget((t) => {
                  t.stroke = v;
                  t.height = t.stroke + t.arrows.stroke + 20;
                })
              }
              defaultValue={1}
            />
          </LabelledRow>
          <LabelledRow label={t('arrow-stroke')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={1}
              max={30}
              tooltipContent={t('arrow-stroke')}
              value={targetArrows?.stroke}
              onChange={(v) => {
                updateTarget((t) => {
                  t.arrows.stroke = v;
                  t.height = t.stroke + t.arrows.stroke + 20;
                })
              }}
              defaultValue={1}
            />
          </LabelledRow>
          <LabelledRow label={t('arrow-strokelength')}>
            <NavbarInputNumber
              width={PANEL_INPUT_WIDTH}
              min={1}
              max={40}
              tooltipContent={t('arrow-strokelength')}
              // Sans réglage, la pointe suit l'épaisseur (cf. getShapeComponent) :
              // on affiche cette longueur plutôt qu'un champ vide.
              value={
                targetArrows?.strokeLength ??
                Math.max(1, Math.round(shapeRadius('triangle', targetArrows?.stroke ?? 1)))
              }
              onChange={(v) => {
                updateTarget((t) => {
                  t.arrows.strokeLength = v;
                })
              }}
              defaultValue={1}
            />
          </LabelledRow>
        </>
      )}

      {/* Ombre : la sous-forme l'affiche avec sa position/taille/rotation
          (cf. positionSizeRotationBlock), la forme simple seule. La box
          composée hors édition riche n'a pas d'ombre à régler ici. */}
      {!isCompound && shadowSection}
      {richEditing && isCompound && (
        <>
          {shadowSection}
          {positionSizeRotationBlock}
        </>
      )}

      {/* Bibliothèque de formes composées : enregistrer la sélection courante
          (formats composés uniquement — une forme simple n'a pas la structure
          `content.shapes` attendue par Proposals::Shape), et/ou en insérer une,
          ce qui remplace toujours la box (comme la banque de médias sur une
          image). Hors édition riche, un Collapse repliable (même traitement
          qu'avant) ; en édition riche, un séparateur de section — ce panneau
          n'a pas de titre "FORME" (cf. ShapePanelSeparator plus haut) auquel
          se raccrocher pour rester repliable sans perdre en lisibilité. */}
      {!richEditing ? (
        <Collapse
          size='small'
          ghost
          items={[
            {
              key: 'shape-library',
              label: t('shape-library'),
              children: (
                <>
                  {canSaveToLibrary && librarySaveRow}
                  <ShapeBankPicker onSelectShape={handleInsertShapeFromLibrary} />
                </>
              ),
            },
          ]}
        />
      ) : (
        <>
          <ShapePanelSeparator title={t('shape-library')} />
          {canSaveToLibrary && librarySaveRow}
          <ShapeBankPicker onSelectShape={handleInsertShapeFromLibrary} />
        </>
      )}

      {/* Choix irréconciliable, posé une fois le fichier choisi (cf.
          applySvgAsShape/applySvgAsImage) : recolorable (perd les couleurs et
          détails d'origine, n'en garde qu'un contour) ou fidèle aux couleurs
          d'origine (mais plus recolorable/éditable comme une forme). */}
      <Modal
        open={!!pendingSvgImport}
        title={t('svg-import-choice-title')}
        onCancel={() => setPendingSvgImport(null)}
        footer={null}
        destroyOnHidden
        modalRender={(node) => <div data-outside-ignore="true">{node}</div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          <Button onClick={applySvgAsShape} style={{ height: 'auto', textAlign: 'left', padding: 12 }}>
            <div style={{ fontWeight: 600 }}>{t('svg-import-as-shape')}</div>
            <div style={{ fontSize: 12, color: 'rgba(11,34,57,0.6)', whiteSpace: 'normal' }}>{t('svg-import-as-shape-hint')}</div>
          </Button>
          <Button onClick={applySvgAsImage} style={{ height: 'auto', textAlign: 'left', padding: 12 }}>
            <div style={{ fontWeight: 600 }}>{t('svg-import-as-image')}</div>
            <div style={{ fontSize: 12, color: 'rgba(11,34,57,0.6)', whiteSpace: 'normal' }}>{t('svg-import-as-image-hint')}</div>
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default React.memo(Shape);
